/**
 * RecentList — reusable list component for recent payments / upcoming sessions.
 *
 * data-testid: candeza-dashboard-{listId}-list
 * Loading state: candeza-dashboard-{listId}-loading
 * Empty state: candeza-dashboard-{listId}-empty
 * Row: candeza-dashboard-{listId}-row-{index}
 */

import React from 'react';
import styled from 'styled-components';

export interface RecentListItem {
  id: string;
  primary: string;
  secondary?: string;
  right?: string;
  meta?: string;
}

export interface RecentListProps {
  listId: string;
  title: string;
  items: RecentListItem[];
  loading?: boolean;
  error?: boolean;
  emptyMessage?: string;
}

const Container = styled.div``;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const Left = styled.div`
  flex: 1;
`;

const Primary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Secondary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const Right = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  text-align: right;
`;

const Meta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary || theme.colors.textSecondary};
  text-align: right;
  margin-top: 2px;
`;

const SkeletonRow = styled.div`
  height: 48px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

export const RecentList: React.FC<RecentListProps> = ({
  listId,
  title,
  items,
  loading = false,
  error = false,
  emptyMessage = 'No items to display',
}) => {
  const baseTestId = `candeza-dashboard-${listId}`;
  return (
    <Container data-testid={`${baseTestId}-list`}>
      <Title>{title}</Title>
      {loading && (
        <>
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} data-testid={`${baseTestId}-loading`} />
          ))}
        </>
      )}
      {!loading && error && (
        <Empty data-testid={`${baseTestId}-error`}>Failed to load data</Empty>
      )}
      {!loading && !error && items.length === 0 && (
        <Empty data-testid={`${baseTestId}-empty`}>{emptyMessage}</Empty>
      )}
      {!loading &&
        !error &&
        items.map((item, idx) => (
          <Row key={item.id} data-testid={`${baseTestId}-row-${idx}`}>
            <Left>
              <Primary>{item.primary}</Primary>
              {item.secondary && <Secondary>{item.secondary}</Secondary>}
            </Left>
            {(item.right || item.meta) && (
              <div>
                {item.right && <Right>{item.right}</Right>}
                {item.meta && <Meta>{item.meta}</Meta>}
              </div>
            )}
          </Row>
        ))}
    </Container>
  );
};

export default RecentList;

